/**
 * One place that decides how a VRM lookup becomes a `vehicles` row.
 *
 * Every screen that can create a vehicle used to do this mapping inline, and
 * each one dropped a different subset of what the lookup had already fetched:
 * the customer modal kept make/year/engine, the vehicle modal kept make/model/
 * year/MOT, the diagnostic modal kept make/model. Colour and fuel type were
 * kept by nobody. The user-visible symptom was a reg that "found itself" and
 * then had to be re-typed on the next screen — and re-typing is how duplicate
 * records get made.
 *
 * The lookup value arrives in one of two shapes: DVLA's (camelCase, from the
 * edge function) or the database's (snake_case, when the vehicle was already
 * known locally). Both are handled here so no caller has to care which it got.
 */

import type { FinalVehicleData } from '../components/vehicles/VRMLookup';

/** The subset of `public.vehicles` a lookup can populate. */
export interface VehicleColumns {
    vrm: string;
    make: string | null;
    model: string | null;
    year: number | null;
    engine: string | null;
    colour: string | null;
    fuel_type: string | null;
    mot_expiry: string | null;
    mileage: number | null;
}

/**
 * Registrations are compared without spaces and in upper case throughout the
 * app (and inside `fn_create_customer_and_vehicle`). Normalising on the way in
 * keeps "ab12 cde" and "AB12CDE" from becoming two vehicles.
 */
export const normalizeVrm = (vrm: string): string =>
    vrm.trim().toUpperCase().replace(/\s+/g, '');

/** Empty string is not a value — store NULL so COALESCE upserts keep working. */
const text = (v: unknown): string | null => {
    if (v === null || v === undefined) return null;
    const s = String(v).trim();
    return s === '' ? null : s;
};

const int = (v: unknown): number | null => {
    const s = text(v);
    if (s === null) return null;
    const n = parseInt(s, 10);
    return Number.isFinite(n) ? n : null;
};

/** A date column wants a bare date, not the timestamp DVLA sometimes returns. */
const date = (v: unknown): string | null => {
    const s = text(v);
    return s === null ? null : s.slice(0, 10);
};

export function vehicleColumnsFromLookup(v: FinalVehicleData): VehicleColumns {
    return {
        vrm: normalizeVrm(v.vrm),
        make: text(v.make),
        model: text(v.model),
        // DVLA sends yearOfManufacture; a local record already has `year`.
        year: int(v.year ?? v.yearOfManufacture),
        // Likewise engine vs engineCapacity (which is a number, in cc).
        engine: text(v.engine ?? v.engineCapacity),
        colour: text(v.colour),
        fuel_type: text(v.fuel_type ?? v.fuelType),
        // Feeds send-mot-reminders. Dropping it silently opted the vehicle out
        // of MOT reminders even though mot_reminder_enabled defaults to true.
        mot_expiry: date(v.motExpiryDate),
        // Never on a DVLA response — only ever typed by the user.
        mileage: int(v.mileage),
    };
}

/** Today as `YYYY-MM-DD` in local time, to compare with DVLA's bare dates. */
const todayIso = (now: Date): string =>
    `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

/** A date that has gone by. An MOT's expiry day itself is still covered. */
const isPast = (iso: string | null | undefined, now: Date): boolean => {
    const d = date(iso);
    return d !== null && d < todayIso(now);
};

/**
 * A due date that has arrived. Vehicle tax is the opposite of an MOT: DVLA's
 * taxDueDate is the first day NEW tax is needed — taxed to 30 June shows a due
 * date of 1 July — so on the due date itself the car is already untaxed.
 */
const isDue = (iso: string | null | undefined, now: Date): boolean => {
    const d = date(iso);
    return d !== null && d <= todayIso(now);
};

/**
 * Whether a DVLA status earns the green tick.
 *
 * Exact match, never a substring: DVLA's failing values contain the passing
 * word — "Not valid" includes "valid", "Untaxed" includes "taxed" — which is
 * how an expired MOT and an untaxed car were both shown green with a tick.
 * A passing status with a date already gone by is treated as expired too: the
 * status can be a cached lookup, the date cannot lie.
 */
export function motIsValid(status: string | null | undefined, expiry?: string | null, now = new Date()): boolean {
    return text(status)?.toLowerCase() === 'valid' && !isPast(expiry, now);
}

export function taxIsValid(status: string | null | undefined, dueDate?: string | null, now = new Date()): boolean {
    return text(status)?.toLowerCase() === 'taxed' && !isDue(dueDate, now);
}

/**
 * The odometer reading from the vehicle's most recent MOT, in miles.
 *
 * WHY THIS IS NOT `mileage`
 *
 * It is deliberately kept out of `vehicleColumnsFromLookup`, and so out of
 * `vehicles.mileage`. That column pre-fills "current mileage" on the next job,
 * and an MOT reading can be eleven months old — writing it there would put a
 * stale number in front of the user as though it were today's. Migration
 * 20260904090000 draws the same line on the server: the MOT reading is only
 * ever used as a labelled last resort, never copied into `vehicles.mileage`.
 *
 * So this exists for one job: pre-filling a mileage box the user is looking at,
 * next to a note saying where the number came from and when it was taken.
 *
 * KM readings are skipped rather than converted, matching the server. DVSA
 * returns the unit alongside the value and a converted figure would not be the
 * number on the clock.
 *
 * Reads both shapes, like `vehicleColumnsFromLookup`: a fresh lookup carries
 * `motHistory`, a vehicle already on file carries the `mot_last_*` columns.
 * Reading only the first is why a saved car with a known MOT reading came up
 * in the booking modal as though we had no idea of its mileage.
 */
export interface MotOdometerSource {
    motHistory?: FinalVehicleData['motHistory'];
    mot_last_odometer_value?: string | null;
    mot_last_odometer_unit?: string | null;
    mot_last_test_date?: string | null;
}

export function motOdometerMiles(v: MotOdometerSource): number | null {
    const raw = v.motHistory?.lastOdometerValue ?? v.mot_last_odometer_value;
    const unit = v.motHistory ? v.motHistory.lastOdometerUnit : v.mot_last_odometer_unit;
    if (!raw) return null;
    const s = String(raw).trim();
    if (!/^[0-9]{1,7}$/.test(s)) return null;
    if ((unit ?? 'MI').toUpperCase() === 'KM') return null;
    return parseInt(s, 10);
}

/** The MOT reading plus the date it was taken, for an "Use the last MOT reading" offer. */
export function motOdometerOffer(v: MotOdometerSource): { miles: number; testDate: string | null } | null {
    const miles = motOdometerMiles(v);
    if (miles === null) return null;
    return { miles, testDate: v.motHistory?.lastTestDate ?? v.mot_last_test_date ?? null };
}
